import type React from "react";
import { forwardRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
} from "@mui/material";
import { LockClosedIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import type { TextFieldProps } from "@mui/material/TextField";
import type { GenericTableRow } from "./types";
import { renderTableCellValue } from "./types";
import { useGenericTableState } from "./useGenericTableState";
import SortIndicator from "./SortIndicator";
import TableSearchField from "./TableSearchField";

export interface ModernGenericTableProps<Row extends GenericTableRow = GenericTableRow> {
  maxTableHeightPx?: number;
  rows: Row[];
  colHeaders: string[];
  className?: string;
  cellClassName?: string;
  emptyPlaceholder?: React.ReactNode;
  checkAll?: boolean;
  showSearchBar?: boolean;
  filterActivated?: boolean;
  nonSortableColumns?: string[];
  onSelectionChange?: (selectedRowIds: (string | number)[]) => void;
  customSortFunctions?: {
    [key: string]: (a: Row, b: Row, direction: "asc" | "desc") => number;
  };
  selectedRowIds: (string | number)[];
  nonDeselectableRowIds?: (string | number)[];
  customCellRenderers?: {
    [columnHeader: string]: (row: Row) => React.ReactNode;
  };
  columnAlignments?: { [columnHeader: string]: "left" | "center" | "right" };
  showCheckboxesForUnselected?: boolean;
  columnProportions?: { [columnHeader: string]: number | string };
  searchBarProps?: Partial<TextFieldProps>;
  headerCellClasses?: { [columnHeader: string]: string };
}

type ModernGenericTableComponent = (<Row extends GenericTableRow = GenericTableRow>(
  props: ModernGenericTableProps<Row> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement | null) & { displayName?: string };

const ModernGenericTable = forwardRef(function ModernGenericTable<
  Row extends GenericTableRow = GenericTableRow,
>(
  {
    maxTableHeightPx = 0,
    rows,
    colHeaders,
    className = "",
    cellClassName = "",
    emptyPlaceholder = "No data available",
    checkAll = false,
    showSearchBar = false,
    filterActivated = true,
    nonSortableColumns = [],
    onSelectionChange = () => {},
    customSortFunctions = {},
    selectedRowIds,
    nonDeselectableRowIds,
    customCellRenderers,
    columnAlignments,
    showCheckboxesForUnselected = true,
    columnProportions,
    searchBarProps,
    headerCellClasses,
  }: ModernGenericTableProps<Row>,
  ref: React.Ref<HTMLDivElement>,
) {
  const { t } = useTranslation();
  const {
    searchString,
    sortedColumn,
    sortDirection,
    displayRows,
    handleSearch,
    handleSort,
    handleRowCheckboxChange,
    handleCheckAllChange,
  } = useGenericTableState<Row>({
    rows,
    colHeaders,
    customSortFunctions,
    selectedRowIds,
    onSelectionChange,
    nonDeselectableRowIds,
  });

  const headerHeight = 56;

  const rowsToRender = showCheckboxesForUnselected
    ? displayRows
    : displayRows.filter(
        (row) =>
          selectedRowIds.includes(row.id) ||
          (nonDeselectableRowIds && nonDeselectableRowIds.includes(row.id)),
      );

  const getColumnWidth = (header: string, index: number, totalColumns: number): number | string => {
    if (columnProportions && columnProportions[header] !== undefined) {
      return columnProportions[header];
    }
    if (header === "" && showSearchBar) {
      return 220;
    }
    if (index === 0 || index === totalColumns - 1) {
      return 150;
    }
    return "auto";
  };

  const renderHeader = () => (
    <Table style={{ tableLayout: "fixed", width: "100%" }}>
      <colgroup>
        {checkAll && <col style={{ width: 50 }} />}
        {colHeaders.map((header, index) => (
          <col key={index} style={{ width: getColumnWidth(header, index, colHeaders.length) }} />
        ))}
      </colgroup>
      <TableHead>
        <TableRow style={{ height: headerHeight }}>
          {checkAll && (
            <TableCell style={{ padding: "0 8px", width: 50, textAlign: "center" }}>
              <Checkbox
                size="small"
                color="default"
                sx={{
                  color: "gray",
                  "&.Mui-checked": { color: "#00C889" },
                }}
                indeterminate={
                  rowsToRender.length > 0 &&
                  !rowsToRender.every((row) => selectedRowIds.includes(row.id))
                }
                checked={
                  rowsToRender.length > 0 &&
                  rowsToRender.every((row) => selectedRowIds.includes(row.id))
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleCheckAllChange();
                }}
              />
            </TableCell>
          )}
          {colHeaders.map((header, index) => (
            <TableCell
              key={index}
              style={{
                padding: "0 8px",
                width: getColumnWidth(header, index, colHeaders.length),
                textAlign: columnAlignments?.[header] ?? "left",
              }}
            >
              {header === "" && showSearchBar ? (
                <div className="w-full">
                  <TableSearchField
                    value={searchString}
                    onChange={handleSearch}
                    placeholder={`${t("general.languages.search")}...`}
                    width="100%"
                    searchBarProps={searchBarProps}
                  />
                </div>
              ) : (
                <div>
                  <span
                    className={
                      headerCellClasses && headerCellClasses[header]
                        ? headerCellClasses[header]
                        : ""
                    }
                  >
                    {header}
                  </span>
                  {filterActivated && header && !nonSortableColumns.includes(header) && (
                    <IconButton size="small" onClick={() => handleSort(header)}>
                      <SortIndicator active={sortedColumn === header} direction={sortDirection} />
                    </IconButton>
                  )}
                </div>
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    </Table>
  );

  const renderBody = () => (
    <div
      style={{ overflowY: "auto", maxHeight: maxTableHeightPx - headerHeight }}
      className="personalize-scroll-visible"
    >
      <Table style={{ tableLayout: "fixed", width: "100%" }}>
        <colgroup>
          {checkAll && <col style={{ width: 50 }} />}
          {colHeaders.map((header, index) => (
            <col key={index} style={{ width: getColumnWidth(header, index, colHeaders.length) }} />
          ))}
        </colgroup>
        <TableBody>
          {rowsToRender.length > 0 ? (
            rowsToRender.map((row, index) => (
              <TableRow
                key={row.id || index}
                hover
                className="h-16"
                style={{
                  backgroundColor:
                    nonDeselectableRowIds && nonDeselectableRowIds.includes(row.id)
                      ? "rgba(0,200,137,0.2)"
                      : undefined,
                }}
              >
                {checkAll && (
                  <TableCell
                    style={{
                      width: 50,
                      padding: "0 8px",
                      textAlign: "center",
                    }}
                  >
                    {nonDeselectableRowIds && nonDeselectableRowIds.includes(row.id) ? (
                      <Checkbox
                        size="small"
                        color="default"
                        checked
                        disabled
                        icon={<LockClosedIcon className="w-4 h-4" />}
                        checkedIcon={<LockClosedIcon className="w-4 h-4" />}
                      />
                    ) : (
                      <Checkbox
                        size="small"
                        color="default"
                        sx={{
                          color: "gray",
                          "&.Mui-checked": { color: "#00C889" },
                        }}
                        checked={selectedRowIds.includes(row.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowCheckboxChange(row.id);
                        }}
                      />
                    )}
                  </TableCell>
                )}
                {colHeaders.map((header, index) => (
                  <TableCell
                    key={index}
                    style={{
                      padding: "0 8px",
                      width: getColumnWidth(header, index, colHeaders.length),
                      textAlign: columnAlignments?.[header] ?? "left",
                    }}
                    className={cellClassName}
                  >
                    {customCellRenderers && customCellRenderers[header]
                      ? customCellRenderers[header](row)
                      : renderTableCellValue(row[header])}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={checkAll ? colHeaders.length + 1 : colHeaders.length}
                align="center"
              >
                {emptyPlaceholder}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <TableContainer
      component={Paper}
      ref={ref}
      className={className}
      style={{ maxHeight: maxTableHeightPx, overflow: "hidden" }}
    >
      {renderHeader()}
      {renderBody()}
    </TableContainer>
  );
}) as ModernGenericTableComponent;

ModernGenericTable.displayName = "ModernGenericTable";
export default ModernGenericTable;
